import React, { useEffect, useState, useMemo, useCallback, ReactNode } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  addFaqCategory,
  fetchFaqCategoryAdmin,
  updateFaqCategory,
} from "slices/faq/thunk";


import { Link } from "react-router-dom";
import CategoriesContainer from "./categorycontainer";
import CategoryForm from "./categoryform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

const selectFaqState = (state: RootState) => (state as any).faq;

const faqSelector = createSelector(selectFaqState, (faq) => ({
  loading: faq.loading,
  pagination: faq.pagination,
  categories: faq.categories, // <-- API Response: data: [...]
}));

const CategoriesPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { categories, loading, pagination } = useSelector(faqSelector);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editFaqData, setEditFaqData] = useState<any>(null);

  // 🔹 Load Categories First Time
  useEffect(() => {
    dispatch(fetchFaqCategoryAdmin());
  }, [dispatch, page, limit]);

  // 🔹 Search Filter Effect
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchFaqCategoryAdmin());
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);  useEffect(() => setPage(1), [searchQuery]);


 const handlePageChange = (newPage: number) => setPage(newPage);
    const validation = useFormik({
    initialValues: {
        category_name: "",
        category_order: "",
        status: "",
    },
    validationSchema: Yup.object({
        category_name: Yup.string()
          .required("Category name is required")
          .matches(/^[a-zA-Z0-9/\s-]+$/, "Category name can only contain alphanumeric characters, spaces, / and -"),
        category_order: Yup.number().required("Order is required"),
        status: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
        try {
        setIsModalOpen(false);

        if (isEdit && editFaqData) {
           const res = await dispatch(updateFaqCategory(editFaqData.id, values));
             if (!res?.success) {
              toast.error("FAQ Category order already exits.");
              return; 
            }
            toast.success("Category updated successfully!");
        } else {
            const res = await dispatch(addFaqCategory(values));
            if (!res?.success) {
              toast.error(res?.message || "Failed to add FAQ question");
              return;
            }
            toast.success("Category added successfully!");
        }

        await dispatch(fetchFaqCategoryAdmin());

        resetForm();
        setIsEdit(false);
        setEditFaqData(null);
        } catch (err) {
        console.error("Error saving FAQ:", err);
        }
    },
    });

    useEffect(() => {
    const handler = setTimeout(() => {
        dispatch(
        fetchFaqCategoryAdmin()
        );
    }, 500);

    return () => clearTimeout(handler);
    }, [dispatch, page, limit, searchQuery, validation.values.status]);

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm();
      setIsEdit(false);
      setEditFaqData(null);
      dispatch(fetchFaqCategoryAdmin());
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);
  
  const handleEditClick = useCallback(
    (row: any) => {
      setIsEdit(true);
      setEditFaqData(row);
      setIsModalOpen(true);

      validation.setValues({
        category_name: row.category_name || "",
        category_order: row.category_order || "",
        status: row.status || "Active",
      });
    },
    [validation]
  );

//   const filteredFaqs = useMemo(() => {
//     const q = searchQuery.trim().toLowerCase();
//     if (!q) return faqList;
//     return faqList.filter(
//       (t: any) =>
//         t.question?.toLowerCase().includes(q) ||
//         t.answer?.toLowerCase().includes(q)
//     );
//   }, [faqList, searchQuery]);

  const columns = useMemo(
    () => [
      { header: "Category ID",
        accessorKey: "id",
        cell: ({ row }: any) => {
          const id = row.original.id;
          return `CAT${String(id).padStart(3, "0")}`;
        },
      },
      { header: "Category Name", accessorKey: "category_name" },
      { header: "Order", accessorKey: "category_order" },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (cell: any) => {
            const value = cell.getValue();
            const lowerVal = value?.toString().toLowerCase();

            const statusColor = lowerVal === "active" ? "#159E3A" : "#A65F00"; 

            return (
            <span
                style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
                }}
            >
                {value}
            </span>
            );
        },
      },
      {
        header: "Actions",
        accessorKey: "actions",
        cell: (cellProps: any) => {
          const row = cellProps.row.original;
          return (
            <ul className="list-inline hstack gap-1 mb-0">
              <li className="list-inline-item" title="Edit">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(row);
                  }}
                >
                  <i className="ri-pencil-fill"></i>
                </Button>
              </li>
            </ul>
          );
        },
      },
    ],
    [handleEditClick]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="FAQs" pageTitle={["FAQs", "FAQs"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={2}>
            <div className="position-relative">
            <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={""}
                placeholder="Filter"
                disabled
            />
            <i
                className="ri-filter-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
            />
            </div>
         </Col>
          <Col md={3}>
            <div className="position-relative">
            <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Faq..."
                onChange={(e) => setSearchQuery(e.target.value)}
            />
            <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
            />
            </div>
         </Col>
          <Col md={7} className="text-end d-flex justify-content-end gap-2 align-items-center">
            <Button color="primary" outline tag={Link} to="/faq">
            <i className="ri-stack-line align-middle me-1" />
              View FAQs
            </Button>
            <Button color="primary" onClick={toggleModal}>
              + Add New Category
            </Button>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  categories.length > 0 ? (
                    <CategoriesContainer
                      columns={columns}
                      data={categories}
                      pagination={pagination}
                      limit={limit}
                      setLimit={setLimit}
                      setPage={setPage}
                      onPageChange={(p) => setPage(p)}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white border-top border-success"   
                      trClass=""
                      divClass="table-responsive table-card"
                        // SearchPlaceholder="Search courses..." columns={columns}                       
                    />
                  ) : (
                    <div className="text-center p-4">No records found.</div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalBody>
          <CategoryForm
            validation={validation}
            isEdit={isEdit}
            categories={categories}         
        />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default CategoriesPage;
